import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2, MessageSquare } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const SUGGEST = ["Should I hedge my open position now?", "Is today a good day for an iron condor?", "Where would you put a stop on my spread?"];

const Rich = ({ text }) => (
  <>{text.split("\n").map((line, i) => (
    <p key={i} className="min-h-[0.5rem]">{line.split(/(\*\*[^*]+\*\*)/g).map((p, j) => (p.startsWith("**") ? <b key={j}>{p.slice(2, -2)}</b> : p))}</p>
  ))}</>
);

export const AdvisorChat = () => {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get("/advisor/chat").then((r) => setMsgs(r.data.messages)).catch(() => {}); }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs, busy]);
  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (question.length < 2 || busy) return;
    setBusy(true); setQ("");
    setMsgs((m) => [...m, { id: "pending", role: "user", text: question }]);
    try {
      const r = (await api.post("/advisor/ask", { question }, { timeout: 120000 })).data;
      setMsgs((m) => [...m.filter((x) => x.id !== "pending"), ...r.messages]);
    } catch (e) {
      toast.error(errMsg(e)); setMsgs((m) => m.filter((x) => x.id !== "pending")); setQ(question);
    } finally { setBusy(false); }
  };
  const clear = async () => { await api.delete("/advisor/chat"); setMsgs([]); };
  return (
    <div className="mt-4 border-t border-slate-800 pt-3" data-testid="advisor-chat">
      <div className="flex items-center gap-2">
        <span className="eyebrow flex items-center gap-1.5"><MessageSquare size={12} /> Ask the Advisor · uses fresh data and your open positions</span>
        {msgs.length > 0 && (
          <button data-testid="button-advisor-chat-clear" onClick={clear} className="ml-auto flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-300">
            <Trash2 size={11} /> Clear today
          </button>
        )}
      </div>
      {msgs.length > 0 && (
        <div className="mt-2 max-h-80 space-y-2 overflow-y-auto pr-1" data-testid="advisor-chat-messages">
          {msgs.map((m) => (
            <div key={m.id} data-testid={`advisor-chat-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${m.role === "user" ? "bg-indigo-500 text-white" : "border border-slate-800 bg-slate-900 text-slate-200"}`}>
                {m.role === "user" ? m.text : <Rich text={m.text} />}
              </div>
            </div>
          ))}
          {busy && <div className="text-[11px] text-slate-500" data-testid="advisor-chat-thinking">Reading today's market and your positions…</div>}
          <div ref={end} />
        </div>
      )}
      {msgs.length === 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SUGGEST.map((s, i) => (
            <button key={s} data-testid={`advisor-chat-suggest-${i}`} disabled={busy} onClick={() => ask(s)}
              className="rounded-full border border-slate-700 px-2.5 py-1 text-[11px] text-slate-400 transition-colors hover:border-indigo-500 hover:text-indigo-200">{s}</button>
          ))}
        </div>
      )}
      <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input data-testid="input-advisor-question" value={q} onChange={(e) => setQ(e.target.value)} maxLength={600} disabled={busy}
          placeholder='e.g. "should I hedge now?"'
          className="h-9 flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none" />
        <button data-testid="button-advisor-ask" type="submit" disabled={busy || q.trim().length < 2}
          className="flex h-9 items-center gap-1.5 rounded-md bg-indigo-500 px-3 text-xs font-semibold text-white transition-colors hover:bg-indigo-400 disabled:opacity-50">
          <Send size={13} /> {busy ? "Thinking…" : "Ask"}
        </button>
      </form>
    </div>
  );
};
